import { EmergenceIcon } from '@/components/icons/emergence-icon'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { AgentRegistryType, useAgentRegistryQuery } from '@/hooks/api/use-agent-registry-query'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { T, useTranslate } from '@tolgee/react'
import { HelpCircle } from 'lucide-react'
import React from 'react'
import { useAuth } from '../_auth/auth-hooks'
import { AgentsOptionsMenu } from '../_common/agent-options-menu'
import { PageTemplate } from '../_common/page-template'

type AgentRegistryTableDef = {
  id: string
  name: string
  description: string
  type: AgentRegistryType
}

export function AgentRegistryTable() {
  const { t } = useTranslate()
  const { user } = useAuth()
  const agentRegistryQuery = useAgentRegistryQuery()

  // Helper function to get the display text for agent author
  const getAuthorDisplayText = React.useCallback(
    (type: AgentRegistryType): string => {
      return type === 'worker' ? t('agentRegistry.table.author.emergence') : user?.fullName ?? ''
    },
    [t, user?.fullName],
  )

  const columnDefs = React.useMemo<ColumnDef<AgentRegistryTableDef>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='agentRegistry.table.headers.name'>
            <T keyName='agentRegistry.table.headers.name' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return <p className='font-light'>{value}</p>
        },
      },
      {
        accessorKey: 'description',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='agentRegistry.table.headers.description'>
            <T keyName='agentRegistry.table.headers.description' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return (
            <p title={value} className='font-light'>
              {value}
            </p>
          )
        },
        size: 400,
      },
      {
        accessorKey: 'type',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='agentRegistry.table.headers.author'>
            <T keyName='agentRegistry.table.headers.author' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<AgentRegistryType>()
          const displayText = getAuthorDisplayText(value)

          return (
            <div className='flex items-center gap-2'>
              {value === 'worker' ? (
                <>
                  <EmergenceIcon className='size-5 flex-shrink-0' />
                  <span className='text-xs font-light'>{displayText}</span>
                </>
              ) : ['generated', 'workflow', 'mcp'].includes(value) ? (
                <>
                  <span className='flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-neutral-600 text-xs font-normal text-neutral-white'>
                    {user?.firstName?.[0]}
                  </span>
                  <span className='line-clamp-1 text-xs font-light'>{displayText}</span>
                </>
              ) : null}
            </div>
          )
        },
        sortingFn: (rowA, rowB) => {
          const typeA = rowA.getValue<AgentRegistryType>('type')
          const typeB = rowB.getValue<AgentRegistryType>('type')

          // Get display values for sorting using the shared helper
          const displayA = getAuthorDisplayText(typeA)
          const displayB = getAuthorDisplayText(typeB)

          // Case-insensitive string comparison
          return displayA.toLowerCase().localeCompare(displayB.toLowerCase())
        },
      },
      {
        id: 'actions',
        size: 20,
        header: () => (
          <div className='flex items-center justify-center gap-1.5 pr-4'>
            <T keyName='agentRegistry.table.headers.actions' />
            <TooltipIconButton
              asChild
              size='auto'
              className='size-[18px] hover:bg-transparent focus:bg-transparent active:bg-transparent'
              keyName='agentRegistry.table.actionsHelp'
            >
              <HelpCircle className='shrink-0 text-neutral-150' />
            </TooltipIconButton>
          </div>
        ),
        cell: ({ row }) => (
          <div className='flex w-full items-center justify-center pr-4'>
            <AgentsOptionsMenu
              agentId={row.original.id}
              agentType={row.original.type}
              agentName={row.original.name}
            />
          </div>
        ),
      },
    ],
    [t, user, getAuthorDisplayText],
  )

  const tableData = React.useMemo(
    () =>
      agentRegistryQuery.data?.data?.map((w) => ({
        id: w.id,
        name: w.name,
        description: w.description,
        type: w.type,
      })) ?? [],
    [agentRegistryQuery.data?.data],
  )

  const table = useReactTable<AgentRegistryTableDef>({
    data: tableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    enableFilters: false,
    sortDescFirst: true,
  })

  const { rows } = table.getRowModel()
  const isTableLoading =
    agentRegistryQuery.status === 'pending' || (agentRegistryQuery.isFetching && rows.length === 0)

  return (
    <div className={cn('flex w-full flex-col gap-6')}>
      <PageTemplate.Header>
        <div>
          <PageTemplate.H4 className='font-normal text-neutral-500'>
            <T keyName='agentRegistry.subtitle' />
          </PageTemplate.H4>
        </div>
      </PageTemplate.Header>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: `${header.getSize()}px` }}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && <TableSkeletonRows cellCount={columnDefs.length} />}
          {agentRegistryQuery.status === 'error' && <TableErrorRow colSpan={columnDefs.length} />}
          {agentRegistryQuery.status === 'success' &&
            rows.length > 0 &&
            rows.map((row) => (
              <TableRow key={row.id} className='h-[72px] bg-neutral-white'>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(`w-[${cell.column.getSize()}px]`, {
                      [`max-w-[240px]`]: cell.column.id === 'name',
                    })}
                    data-state={
                      (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {agentRegistryQuery.status === 'success' &&
            rows?.length === 0 &&
            !agentRegistryQuery.isFetching && (
              <TableRow>
                <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                  No results.
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </Table>
    </div>
  )
}
